import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

const palette = {
  axis: "#64748b",
  grid: "#e2e8f0",
  line: "#2563eb",
  area: "rgba(37, 99, 235, 0.08)",
  bar: "#2563eb",
  barHover: "#1d4ed8",
  barMuted: "rgba(37, 99, 235, 0.18)",
  barSeries: ["#2563eb", "#0f766e", "#d97706", "#dc2626", "#7c3aed", "#db2777"],
  dot: "#2563eb",
  dotMuted: "rgba(37, 99, 235, 0.20)",
  focus: "#0f172a",
  focusSoft: "rgba(0, 97, 255, 0.10)"
}

const fontStyle = '11px "Geist Mono", monospace'

function xScaleValue(scale, value) {
  return scale(value) ?? 0
}

function formatTickNumber(value) {
  if (Math.abs(value) >= 1_000_000) return d3.format(".2s")(value)
  if (Math.abs(value) >= 1_000) return d3.format(",.0f")(value)
  if (Number.isInteger(value)) return d3.format(",")(value)
  return d3.format(",.1f")(value)
}

export default class extends Controller {
  static values = {
    rows: Array,
    spec: Object,
    chartType: String
  }

  connect() {
    this.element.classList.add("chart-frame")
    this.render()
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = requestAnimationFrame(() => this.render())
    })
    this.resizeObserver.observe(this.element)
  }

  disconnect() {
    this.hideTooltip()
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.resizeFrame)
  }

  render() {
    const rows = this.rowsValue || []
    const spec = this.specValue || {}
    const chartType = this.chartTypeValue || spec.default_chart_type || "table"

    this.element.innerHTML = ""
    this.tooltip = null

    if (!rows.length || chartType === "table") {
      this.renderEmpty(chartType)
      return
    }

    const width = Math.max(this.element.clientWidth || 640, 320)
    const height = 400
    const margin = { top: chartType === "bar" ? 56 : 16, right: 16, bottom: 40, left: 54 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom
    const svg = d3.create("svg")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("class", "w-full overflow-visible")
      .attr("role", "img")
    const root = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`)
    const xKey = spec.mappings?.x
    const yKey = spec.mappings?.y
    const sizeKey = spec.mappings?.size || null
    const labelKey = spec.mappings?.label || null
    const tooltipExtra = spec.mappings?.tooltip_extra || []

    if (!xKey || !yKey) {
      this.renderEmpty("table")
      return
    }

    const titleJoiner = chartType === "scatter" ? "vs" : "by"
    svg.append("title").text(`${yKey} ${titleJoiner} ${xKey}`)
    svg.append("desc").text(`${chartType} chart showing ${yKey} ${titleJoiner} ${xKey}, ${rows.length} data points`)

    if (chartType === "bar") {
      this.renderBarChart(root, rows, innerWidth, innerHeight, xKey, yKey, { sizeKey, labelKey, tooltipExtra })
    } else if (chartType === "line") {
      this.renderLineChart(root, rows, innerWidth, innerHeight, xKey, yKey, { sizeKey, labelKey, tooltipExtra })
    } else if (chartType === "scatter") {
      this.renderScatterPlot(root, rows, innerWidth, innerHeight, xKey, yKey, { sizeKey, labelKey, tooltipExtra })
    } else {
      this.renderEmpty(chartType)
      return
    }

    this.element.append(svg.node())
  }

  renderBarChart(root, rows, width, height, xKey, yKey, { sizeKey, labelKey, tooltipExtra } = {}) {
    const data = rows.filter((row) => row[xKey] != null && row[yKey] != null)
    if (!data.length) { this.renderEmpty("table"); return }

    const { categoryKey, seriesKeys } = this.resolveBarChartStructure(data, { xKey, yKey, sizeKey, labelKey, tooltipExtra })
    const categories = data.map((row) => `${row[categoryKey]}`)
    const x = d3.scaleBand().domain(categories).range([0, width]).padding(0.28)

    if (seriesKeys.length > 1) {
      const stackedRows = data.map((row) => (
        seriesKeys.reduce((memo, key) => {
          memo[key] = +row[key] || 0
          return memo
        }, { category: `${row[categoryKey]}`, raw: row })
      ))
      const stack = d3.stack().keys(seriesKeys)
      const stackedSeries = stack(stackedRows)
      const maxValue = d3.max(stackedRows, (row) => d3.sum(seriesKeys, (key) => row[key])) || 0
      const y = d3.scaleLinear().domain([0, maxValue]).nice().range([height, 0])
      const color = d3.scaleOrdinal().domain(seriesKeys).range(palette.barSeries)

      this.addLegend(root, seriesKeys, color, width)
      this.addHorizontalGrid(root, width, y)

      const segments = root.append("g")
        .selectAll("g")
        .data(stackedSeries)
        .join("g")
        .attr("fill", (series) => color(series.key))
        .selectAll("rect")
        .data((series) => series.map((segment) => ({ ...segment, key: series.key })))
        .join("rect")
        .attr("x", (segment) => x(segment.data.category))
        .attr("y", (segment) => y(segment[1]))
        .attr("width", x.bandwidth())
        .attr("height", (segment) => Math.max(0, y(segment[0]) - y(segment[1])))
        .attr("rx", 0)
        .attr("class", "chart-mark")
        .attr("tabindex", 0)

      this.addMarkInteraction(segments, {
        xKey: categoryKey,
        yKey,
        sizeKey,
        labelKey,
        tooltipExtra,
        tooltipBuilder: (segment) => this.buildStackedBarTooltip(segment.data.raw, categoryKey, segment.key, seriesKeys),
        highlight(node) {
          segments.attr("stroke", "none").attr("stroke-width", 0)
          d3.select(node).attr("stroke", "rgba(255,255,255,0.95)").attr("stroke-width", 1.5)
        },
        reset() {
          segments.attr("stroke", "none").attr("stroke-width", 0)
        }
      })

      this.addAxes(root, width, height, x, y)
      return
    }

    const valueKey = seriesKeys[0] || yKey
    const y = d3.scaleLinear().domain([0, d3.max(data, (row) => +row[valueKey]) || 0]).nice().range([height, 0])

    this.addHorizontalGrid(root, width, y)

    const bars = root.append("g")
      .selectAll("rect")
      .data(data)
      .join("rect")
      .attr("x", (row) => x(`${row[categoryKey]}`))
      .attr("y", (row) => y(+row[valueKey]))
      .attr("width", x.bandwidth())
      .attr("height", (row) => height - y(+row[valueKey]))
      .attr("rx", 2)
      .attr("fill", palette.bar)
      .attr("class", "chart-mark")
      .attr("tabindex", 0)

    this.addMarkInteraction(bars, {
      data, xKey: categoryKey, yKey: valueKey, sizeKey, labelKey, tooltipExtra,
      highlight(node) {
        bars.attr("fill", palette.barMuted)
        d3.select(node).attr("fill", palette.barHover)
      },
      reset() {
        bars.attr("fill", palette.bar)
      }
    })

    this.addAxes(root, width, height, x, y)
  }

  renderLineChart(root, rows, width, height, xKey, yKey, { sizeKey, labelKey, tooltipExtra } = {}) {
    const parsed = rows
      .filter((row) => row[xKey] != null && row[yKey] != null)
      .map((row) => ({
        x: this.parseTemporal(row[xKey]) || row[xKey],
        y: +row[yKey],
        raw: row
      }))
      .sort((a, b) => (a.x > b.x ? 1 : -1))

    if (!parsed.length) { this.renderEmpty("table"); return }

    const x = parsed[0]?.x instanceof Date
      ? d3.scaleTime().domain(d3.extent(parsed, (row) => row.x)).range([0, width])
      : d3.scalePoint().domain(parsed.map((row) => `${row.x}`)).range([0, width]).padding(0.5)

    const [yMin, yMax] = d3.extent(parsed, (row) => row.y)
    const yRange = yMax - yMin
    const y = d3.scaleLinear()
      .domain([Math.min(0, yMin), yMax + yRange * 0.05])
      .nice()
      .range([height, 0])

    this.addHorizontalGrid(root, width, y)

    const area = d3.area()
      .x((row) => xScaleValue(x, row.x))
      .y0(height)
      .y1((row) => y(row.y))
      .curve(d3.curveMonotoneX)

    const line = d3.line()
      .x((row) => xScaleValue(x, row.x))
      .y((row) => y(row.y))
      .curve(d3.curveMonotoneX)

    root.append("path").datum(parsed).attr("fill", palette.area).attr("d", area)
    root.append("path").datum(parsed).attr("fill", "none").attr("stroke", palette.line).attr("stroke-width", 2).attr("d", line)

    root.append("g")
      .selectAll("circle")
      .data(parsed)
      .join("circle")
      .attr("cx", (row) => xScaleValue(x, row.x))
      .attr("cy", (row) => y(row.y))
      .attr("r", 3.5)
      .attr("fill", "#fff")
      .attr("stroke", palette.line)
      .attr("stroke-width", 1.5)
      .attr("pointer-events", "none")

    const focus = root.append("g").style("display", "none")
    focus.append("line")
      .attr("class", "chart-focus-line")
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", palette.focus)
      .attr("stroke-dasharray", "3 4")
      .attr("stroke-opacity", 0.18)
    focus.append("circle")
      .attr("class", "chart-focus-ring")
      .attr("r", 8)
      .attr("fill", palette.focusSoft)
    focus.append("circle")
      .attr("class", "chart-focus-dot")
      .attr("r", 4.5)
      .attr("fill", "#fff")
      .attr("stroke", palette.line)
      .attr("stroke-width", 2)

    root.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent")
      .on("pointerenter pointermove", (event) => {
        const point = this.closestDatumFromPointer(event, parsed, x, (row) => row.x)
        if (!point) return

        const xPos = xScaleValue(x, point.x)
        const yPos = y(point.y)
        focus.style("display", null)
        focus.select(".chart-focus-line").attr("x1", xPos).attr("x2", xPos)
        focus.select(".chart-focus-ring").attr("cx", xPos).attr("cy", yPos)
        focus.select(".chart-focus-dot").attr("cx", xPos).attr("cy", yPos)
        this.showTooltip(event, this.buildRichTooltip(point.raw, xKey, yKey, sizeKey, labelKey, tooltipExtra))
      })
      .on("pointerleave", () => {
        focus.style("display", "none")
        this.hideTooltip()
      })

    this.addAxes(root, width, height, x, y)
  }

  renderScatterPlot(root, rows, width, height, xKey, yKey, { sizeKey, labelKey, tooltipExtra } = {}) {
    const parsed = rows.filter((row) => row[xKey] != null && row[yKey] != null)
    if (!parsed.length) { this.renderEmpty("table"); return }

    const x = d3.scaleLinear().domain(d3.extent(parsed, (row) => +row[xKey])).nice().range([0, width])
    const y = d3.scaleLinear().domain(d3.extent(parsed, (row) => +row[yKey])).nice().range([height, 0])

    const sizeScale = sizeKey
      ? d3.scaleSqrt()
          .domain(d3.extent(parsed, (row) => +row[sizeKey]))
          .range([4, 20])
      : null

    this.addHorizontalGrid(root, width, y)
    this.addVerticalGrid(root, height, x)

    const radiusFn = sizeScale
      ? (row) => sizeScale(+row[sizeKey])
      : () => 5

    const points = root.append("g")
      .selectAll("circle")
      .data(parsed)
      .join("circle")
      .attr("cx", (row) => x(+row[xKey]))
      .attr("cy", (row) => y(+row[yKey]))
      .attr("r", radiusFn)
      .attr("fill", palette.dot)
      .attr("fill-opacity", 0.6)
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5)
      .attr("class", "chart-mark")
      .attr("tabindex", 0)

    this.addMarkInteraction(points, {
      data: parsed, xKey, yKey, sizeKey, labelKey, tooltipExtra,
      highlight(node) {
        points
          .attr("fill-opacity", 0.2)
          .attr("stroke-width", 1.5)
        d3.select(node)
          .attr("fill-opacity", 0.9)
          .attr("stroke", palette.focus)
          .attr("stroke-width", 2)
      },
      reset() {
        points
          .attr("fill-opacity", 0.6)
          .attr("r", radiusFn)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1.5)
      }
    })

    this.addAxes(root, width, height, x, y)
  }

  // --- Shared helpers ---

  addMarkInteraction(selection, { xKey, yKey, sizeKey, labelKey, tooltipExtra, tooltipBuilder, highlight, reset }) {
    selection
      .on("pointerenter pointermove", (event, row) => {
        highlight(event.currentTarget)
        this.showTooltip(event, tooltipBuilder ? tooltipBuilder(row) : this.buildRichTooltip(row, xKey, yKey, sizeKey, labelKey, tooltipExtra))
      })
      .on("pointerleave", () => {
        reset()
        this.hideTooltip()
      })
      .on("focus", (event, row) => {
        highlight(event.currentTarget)
        this.showTooltipForNode(event.currentTarget, tooltipBuilder ? tooltipBuilder(row) : this.buildRichTooltip(row, xKey, yKey, sizeKey, labelKey, tooltipExtra))
      })
      .on("blur", () => {
        reset()
        this.hideTooltip()
      })
  }

  addLegend(root, keys, colorScale, width) {
    const itemGap = 18
    let cursor = 0

    const legend = root.append("g")
      .attr("class", "chart-legend")
      .attr("transform", "translate(0,-34)")

    keys.forEach((key) => {
      const item = legend.append("g").attr("transform", `translate(${cursor},0)`)

      item.append("rect")
        .attr("x", 0)
        .attr("y", -8)
        .attr("width", 10)
        .attr("height", 10)
        .attr("rx", 2)
        .attr("fill", colorScale(key))

      item.append("text")
        .attr("x", 16)
        .attr("y", 0)
        .attr("fill", palette.axis)
        .style("font", fontStyle)
        .text(key)

      const textWidth = key.length * 7
      cursor += 16 + textWidth + itemGap
    })

    if (cursor > width) {
      legend.attr("transform", "translate(0,-40)")
    }
  }

  resolveBarChartStructure(rows, { xKey, yKey, sizeKey, labelKey, tooltipExtra }) {
    const seriesCandidates = [xKey, yKey, sizeKey, ...(tooltipExtra || [])].filter(Boolean)
    const useLabelAsCategory = labelKey &&
      this.columnLooksCategorical(rows, labelKey) &&
      this.columnLooksNumeric(rows, xKey)

    const categoryKey = useLabelAsCategory ? labelKey : xKey
    const seriesKeys = seriesCandidates.filter((key, index) => (
      key !== categoryKey &&
      seriesCandidates.indexOf(key) === index &&
      this.columnLooksNumeric(rows, key)
    ))

    return { categoryKey, seriesKeys }
  }

  buildRichTooltip(row, xKey, yKey, sizeKey, labelKey, tooltipExtra) {
    const entries = []
    if (labelKey && row[labelKey] != null) entries.push({ label: labelKey, value: row[labelKey] })
    entries.push({ label: xKey, value: row[xKey] })
    entries.push({ label: yKey, value: row[yKey] })
    if (sizeKey && row[sizeKey] != null) entries.push({ label: sizeKey, value: row[sizeKey] })
    if (tooltipExtra) {
      for (const key of tooltipExtra) {
        if (row[key] != null) entries.push({ label: key, value: row[key] })
      }
    }
    return entries
  }

  buildStackedBarTooltip(row, categoryKey, activeSeriesKey, seriesKeys) {
    const entries = [{ label: categoryKey, value: row[categoryKey] }]

    if (activeSeriesKey && row[activeSeriesKey] != null) {
      entries.push({ label: activeSeriesKey, value: row[activeSeriesKey] })
    }

    for (const key of seriesKeys) {
      if (key === activeSeriesKey || row[key] == null) continue
      entries.push({ label: key, value: row[key] })
    }

    return entries
  }

  columnLooksNumeric(rows, key) {
    if (!key) return false

    return rows.some((row) => (
      row[key] != null &&
      typeof row[key] === "number" &&
      Number.isFinite(row[key])
    ))
  }

  columnLooksCategorical(rows, key) {
    if (!key) return false

    return rows.some((row) => {
      const value = row[key]
      return typeof value === "string" || value instanceof Date
    })
  }

  addHorizontalGrid(root, width, y) {
    root.append("g")
      .attr("class", "grid-h")
      .call(d3.axisLeft(y).tickSize(-width).tickFormat(() => ""))
      .call((g) => g.select(".domain").remove())
      .call((g) => g.selectAll("line").attr("stroke", palette.grid))
  }

  addVerticalGrid(root, height, x) {
    root.append("g")
      .attr("class", "grid-v")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).tickSize(-height).tickFormat(() => ""))
      .call((g) => g.select(".domain").remove())
      .call((g) => g.selectAll("line").attr("stroke", palette.grid))
  }

  addAxes(root, width, height, x, y) {
    const xAxis = x.bandwidth
      ? d3.axisBottom(x).tickSizeOuter(0)
      : d3.axisBottom(x).tickSizeOuter(0).ticks(Math.min(width / 80, 10))

    if (!x.bandwidth && !(x.domain?.()[0] instanceof Date)) {
      xAxis.tickFormat(formatTickNumber)
    }

    root.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis)
      .call((g) => g.select(".domain").attr("stroke", palette.grid))
      .call((g) => g.selectAll("line").attr("stroke", palette.grid))
      .call((g) => g.selectAll("text").attr("fill", palette.axis).style("font", fontStyle))

    root.append("g")
      .call(d3.axisLeft(y).ticks(5).tickSizeOuter(0).tickFormat(formatTickNumber))
      .call((g) => g.select(".domain").remove())
      .call((g) => g.selectAll("text").attr("fill", palette.axis).style("font", fontStyle))
      .call((g) => g.selectAll("line").attr("stroke", palette.grid))
  }

  renderEmpty(chartType) {
    this.element.innerHTML = `
      <div class="flex min-h-[20rem] items-center justify-center text-center text-[13px] text-slate-400">
        ${chartType === "table" ? "This dataset is better expressed as a table." : "Needs a clearer x/y mapping for this view."}
      </div>
    `
  }

  closestDatumFromPointer(event, data, scale, accessor) {
    const [pointerX] = d3.pointer(event)

    return data.reduce((closest, row) => {
      const distance = Math.abs(xScaleValue(scale, accessor(row)) - pointerX)
      if (!closest || distance < closest.distance) {
        return { ...row, distance }
      }
      return closest
    }, null)
  }

  parseTemporal(value) {
    if (typeof value !== "string") return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.valueOf()) ? null : parsed
  }

  tooltipElement() {
    if (!this.tooltip) {
      this.tooltip = document.createElement("div")
      this.tooltip.className = "chart-tooltip hidden"
      this.tooltip.setAttribute("role", "status")
      this.tooltip.setAttribute("aria-live", "polite")
      this.element.append(this.tooltip)
    }
    return this.tooltip
  }

  showTooltip(event, entries) {
    const tooltip = this.tooltipElement()
    const bounds = this.element.getBoundingClientRect()
    const x = event.clientX - bounds.left
    const y = event.clientY - bounds.top

    this.renderTooltipContent(entries)
    tooltip.classList.remove("hidden")
    this.positionTooltip(tooltip, x, y)
  }

  showTooltipForNode(node, entries) {
    const tooltip = this.tooltipElement()
    const nodeBounds = node.getBoundingClientRect()
    const bounds = this.element.getBoundingClientRect()
    const x = nodeBounds.left - bounds.left + (nodeBounds.width / 2)
    const y = nodeBounds.top - bounds.top

    this.renderTooltipContent(entries)
    tooltip.classList.remove("hidden")
    this.positionTooltip(tooltip, x, y)
  }

  renderTooltipContent(entries) {
    const tooltip = this.tooltipElement()
    tooltip.innerHTML = entries.map(({ label, value }) => `
      <div class="chart-tooltip__row">
        <span>${this.escapeHtml(label)}</span>
        <strong>${this.escapeHtml(this.formatValue(value))}</strong>
      </div>
    `).join("")
  }

  positionTooltip(tooltip, x, y) {
    const offset = 12
    const maxX = this.element.clientWidth - tooltip.offsetWidth - 8
    const nextX = Math.max(8, Math.min(x + offset, maxX))
    const nextY = Math.max(8, y - tooltip.offsetHeight - offset)

    tooltip.style.left = `${nextX}px`
    tooltip.style.top = `${nextY}px`
  }

  hideTooltip() {
    if (!this.tooltip) return
    this.tooltip.classList.add("hidden")
  }

  formatValue(value) {
    if (value instanceof Date) {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      }).format(value)
    }

    if (typeof value === "number") {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
    }

    const numericValue = Number(value)
    if (typeof value === "string" && value.trim() !== "" && Number.isFinite(numericValue)) {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(numericValue)
    }

    return `${value}`
  }

  escapeHtml(value) {
    return `${value}`
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
